/**
 * PaymentMethodsBadge — bande de logos affichée sous chaque bouton « Payer »
 * pour rassurer l'utilisateur sur les moyens de paiement acceptés.
 *
 * Spec utilisateur 2026-02 (Option A) :
 * - Logos visuels uniquement (non cliquables individuellement) — le bouton
 *   « Payer » au-dessus ouvre Stripe Checkout, qui propose ensuite chacun
 *   de ces moyens de façon fonctionnelle (Apple Pay sur Safari, Google Pay
 *   sur Chrome/Android, PayPal pour tous, carte bleue toujours).
 * - Les logos sont rendus en SVG inline pour rester ultra-légers (aucune
 *   requête réseau) et conformes aux guidelines de marque (proportions et
 *   couleurs officielles 2024-2026).
 */
import React from "react";

const baseClass =
  "inline-flex items-center justify-center h-7 sm:h-8 px-2 sm:px-2.5 bg-white border border-border rounded-md shadow-sm";

const VisaIcon = () => (
  <svg viewBox="0 0 48 16" className="h-3 sm:h-4 w-auto" aria-label="Carte bleue Visa">
    <text x="0" y="13" fontFamily="Arial, sans-serif" fontSize="14" fontWeight="900" fill="#1A1F71" letterSpacing="-0.5">
      VISA
    </text>
  </svg>
);

const MastercardIcon = () => (
  <svg viewBox="0 0 32 20" className="h-3 sm:h-4 w-auto" aria-label="Mastercard">
    <circle cx="12" cy="10" r="8" fill="#EB001B" />
    <circle cx="20" cy="10" r="8" fill="#F79E1B" />
    <path d="M16 4.2a8 8 0 0 0 0 11.6 8 8 0 0 0 0-11.6z" fill="#FF5F00" />
  </svg>
);

const CarteBleueIcon = () => (
  <svg viewBox="0 0 40 24" className="h-3 sm:h-4 w-auto" aria-label="Carte Bleue">
    <rect x="0" y="0" width="40" height="24" rx="3" fill="#0066B3" />
    <text x="20" y="16" textAnchor="middle" fontFamily="Arial, sans-serif" fontSize="11" fontWeight="900" fill="#FFFFFF">
      CB
    </text>
  </svg>
);

const GooglePayIcon = () => (
  <svg viewBox="0 0 60 24" className="h-3.5 sm:h-4 w-auto" aria-label="Google Pay">
    <path d="M9.6 12.2c0-.42-.04-.83-.11-1.22H4.9v2.32h2.65a2.27 2.27 0 0 1-.98 1.49v1.23h1.58c.93-.86 1.46-2.13 1.46-3.82z" fill="#4285F4"/>
    <path d="M4.9 17c1.32 0 2.43-.44 3.24-1.18l-1.58-1.23c-.44.3-1 .47-1.66.47-1.27 0-2.35-.86-2.74-2.02H.53v1.27A4.9 4.9 0 0 0 4.9 17z" fill="#34A853"/>
    <path d="M2.16 13.04A2.94 2.94 0 0 1 2 12c0-.36.06-.71.16-1.04V9.69H.53A4.9 4.9 0 0 0 0 12c0 .8.19 1.55.53 2.31l1.63-1.27z" fill="#FBBC04"/>
    <path d="M4.9 7.96c.72 0 1.36.25 1.87.73l1.4-1.4A4.71 4.71 0 0 0 4.9 6 4.9 4.9 0 0 0 .53 8.69l1.63 1.27A2.94 2.94 0 0 1 4.9 7.96z" fill="#EA4335"/>
    <text x="13" y="16" fontFamily="Arial, sans-serif" fontSize="11" fontWeight="500" fill="#5F6368">Pay</text>
  </svg>
);

const ApplePayIcon = () => (
  <svg viewBox="0 0 60 24" className="h-3.5 sm:h-4 w-auto" aria-label="Apple Pay">
    <path d="M9.5 7.27c-.5.59-1.3.99-2.1 1-.1-.83.34-1.65.84-2.18.55-.6 1.42-1.04 2.07-1.05.08.85-.27 1.66-.81 2.23zm.81 1.04c-1.16-.07-2.14.66-2.7.66-.56 0-1.4-.63-2.32-.61C4.1 8.4 3 9.16 2.4 10.37c-1.27 2.2-.32 5.45.9 7.23.6.86 1.31 1.83 2.25 1.8.9-.04 1.24-.59 2.33-.59 1.08 0 1.4.59 2.34.58.97-.02 1.58-.88 2.17-1.74.69-1 .97-1.97.99-2.02-.02-.01-1.9-.73-1.92-2.9-.02-1.81 1.48-2.68 1.55-2.72-.85-1.25-2.16-1.39-2.62-1.42z" fill="#000000"/>
    <text x="17" y="17" fontFamily="Arial, sans-serif" fontSize="13" fontWeight="500" fill="#000000">Pay</text>
  </svg>
);

const PayPalIcon = () => (
  <svg viewBox="0 0 60 18" className="h-3.5 sm:h-4 w-auto" aria-label="PayPal">
    <path d="M6.3 1.5h3.45c1.86 0 3.18.43 3.97 1.28.79.85.97 2.05.55 3.6-.4 1.45-1.16 2.5-2.27 3.15-1.11.65-2.53.98-4.26.98H6.84l-.55 3.5H4.16L6.3 1.5zm1.96 2.05L7.6 7.66h1.07c.95 0 1.66-.2 2.13-.6.47-.39.78-.99.94-1.78.16-.78.07-1.32-.26-1.62-.34-.3-.97-.45-1.9-.45H8.27z" fill="#003087"/>
    <path d="M16.85 4.85c.84 0 1.5.21 1.97.63.47.42.75.99.83 1.72l.04.39.15-.04h2.07l-1.42 6.46h-1.97l.18-.84-.06.06c-.5.6-1.21.9-2.12.9-.93 0-1.65-.3-2.16-.9-.5-.59-.66-1.42-.47-2.49.19-1.07.66-1.93 1.4-2.58.74-.65 1.66-.97 2.74-.97zm-.45 2.04c-.52 0-.99.18-1.4.55-.4.36-.68.83-.81 1.4-.13.58-.06 1.04.21 1.4.27.36.66.54 1.18.54.51 0 .98-.18 1.4-.54.42-.36.69-.82.82-1.4.13-.58.06-1.04-.21-1.4-.27-.37-.66-.55-1.19-.55z" fill="#003087"/>
    <path d="M28 4.85h2.27l-5.2 9.95H22.8l1.95-3.51-2.18-6.44h2.27l1.16 4.31 1.95-4.31z" fill="#003087"/>
    <path d="M33.6 1.5h3.46c1.85 0 3.17.43 3.96 1.28.8.85.98 2.05.56 3.6-.41 1.45-1.16 2.5-2.27 3.15-1.12.65-2.54.98-4.27.98h-.9l-.54 3.5h-2.13L33.6 1.5zm1.96 2.05l-.66 4.11h1.06c.96 0 1.67-.2 2.13-.6.47-.39.78-.99.94-1.78.16-.78.08-1.32-.26-1.62-.34-.3-.97-.45-1.9-.45h-1.31z" fill="#009CDE"/>
    <path d="M44.15 4.85c.84 0 1.5.21 1.97.63.47.42.75.99.83 1.72l.04.39.15-.04h2.07l-1.42 6.46h-1.97l.19-.84-.06.06c-.5.6-1.22.9-2.13.9-.93 0-1.65-.3-2.15-.9-.5-.59-.66-1.42-.47-2.49.19-1.07.66-1.93 1.4-2.58.74-.65 1.65-.97 2.73-.97zm-.45 2.04c-.52 0-.99.18-1.4.55-.4.36-.67.83-.8 1.4-.14.58-.07 1.04.2 1.4.27.36.67.54 1.19.54.51 0 .98-.18 1.4-.54.42-.36.69-.82.82-1.4.13-.58.06-1.04-.21-1.4-.27-.37-.66-.55-1.2-.55z" fill="#009CDE"/>
    <path d="M51 1.5h2.15l-2.6 12.5h-2.15L51 1.5z" fill="#009CDE"/>
  </svg>
);

export default function PaymentMethodsBadge({ className = "", testId = "payment-methods-badge" }) {
  return (
    <div
      className={`flex flex-col items-center gap-1.5 ${className}`}
      data-testid={testId}
    >
      <p className="text-[10px] uppercase tracking-[0.15em] text-subtle font-bold">
        Paiement 100% sécurisé via Stripe
      </p>
      <div className="flex flex-wrap items-center justify-center gap-1.5 sm:gap-2">
        <span className={baseClass} title="Carte Bleue" data-testid="pm-badge-cb">
          <CarteBleueIcon />
        </span>
        <span className={baseClass} title="Visa" data-testid="pm-badge-visa">
          <VisaIcon />
        </span>
        <span className={baseClass} title="Mastercard" data-testid="pm-badge-mastercard">
          <MastercardIcon />
        </span>
        <span className={baseClass} title="Google Pay" data-testid="pm-badge-gpay">
          <GooglePayIcon />
        </span>
        <span className={baseClass} title="Apple Pay" data-testid="pm-badge-apay">
          <ApplePayIcon />
        </span>
        <span className={baseClass} title="PayPal" data-testid="pm-badge-paypal">
          <PayPalIcon />
        </span>
      </div>
    </div>
  );
}
